/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
import React, { useState, useEffect, useMemo } from "react"
import {
  Modal,
  Button,
  Tag,
  Typography,
  Spin,
  message,
  Empty,
  Tabs,
  Select,
  Tooltip,
} from "antd"
import {
  HolderOutlined,
  FileTextOutlined,
  FolderOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  PlusOutlined,
  SettingOutlined,
  FireOutlined,
  StarOutlined,
} from "@ant-design/icons"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { debounce } from "lodash-es"
import { settingService, type SettingItem } from "@/services/system/settingApi"
import { setService } from "@/services/dictionary/setApi"

const { Text } = Typography

interface SystemSetSettingsModalProps {
  open: boolean
  onClose: () => void
}

interface SortableSetItemProps {
  item: any
  index: number
  totalCount: number
  onMoveUp: () => void
  onMoveDown: () => void
  onRemove: () => void
}

const SortableSetItem: React.FC<SortableSetItemProps> = ({
  item,
  index,
  totalCount,
  onMoveUp,
  onMoveDown,
  onRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 9999 : "auto",
    opacity: isDragging ? 0.75 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center justify-between gap-3 p-3 mb-2 rounded-lg border bg-white transition-all ${
        isDragging
          ? "border-primary shadow-lg bg-blue-50/50"
          : "border-gray-200 hover:border-gray-300 hover:shadow-xs"
      }`}
    >
      {/* Left: Drag handle & index & info */}
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div
          {...attributes}
          {...listeners}
          className="flex items-center justify-center p-1.5 text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing rounded hover:bg-gray-100"
          title="Kéo thả để đổi thứ tự"
        >
          <HolderOutlined className="text-base" />
        </div>

        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
          {index + 1}
        </div>

        <div className="shrink-0 text-base">
          {item.thumbnail ? (
            <img
              src={item.thumbnail}
              alt=""
              className="w-8 h-8 rounded object-cover border border-gray-200"
            />
          ) : (
            <div className="w-8 h-8 rounded bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100">
              <FolderOutlined className="text-sm" />
            </div>
          )}
        </div>

        <div className="flex flex-col min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-800 text-sm truncate max-w-[360px]">
              {item.full_title || item.title || `Thư mục ID #${item.id}`}
            </span>
            <Tag color="geekblue" className="text-[11px] m-0 py-0 px-1 font-mono">
              ID: {item.id}
            </Tag>
            {item.total_words !== undefined && (
              <Tag color="orange" className="text-[11px] m-0 py-0 px-1">
                {item.total_words} từ
              </Tag>
            )}
            {Array.isArray(item.tags) &&
              item.tags.map((t: string) => (
                <Tag key={t} color="cyan" className="text-[10px] m-0 py-0 px-1">
                  {t}
                </Tag>
              ))}
          </div>
          {item.full_title && item.title && item.full_title !== item.title && (
            <Text type="secondary" className="text-xs italic truncate max-w-[400px]">
              Tên ngắn: {item.title}
            </Text>
          )}
        </div>
      </div>

      {/* Right: Quick Up/Down buttons & remove */}
      <div className="flex items-center gap-1 shrink-0">
        <Tooltip title="Di chuyển lên">
          <Button
            type="text"
            size="small"
            icon={<ArrowUpOutlined />}
            disabled={index === 0}
            onClick={(e) => {
              e.stopPropagation()
              onMoveUp()
            }}
            className="text-gray-500 hover:text-primary"
          />
        </Tooltip>
        <Tooltip title="Di chuyển xuống">
          <Button
            type="text"
            size="small"
            icon={<ArrowDownOutlined />}
            disabled={index === totalCount - 1}
            onClick={(e) => {
              e.stopPropagation()
              onMoveDown()
            }}
            className="text-gray-500 hover:text-primary"
          />
        </Tooltip>
        <Tooltip title="Xóa khỏi danh sách">
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
          />
        </Tooltip>
      </div>
    </div>
  )
}

interface SetListEditorProps {
  items: any[]
  onChange: (items: any[]) => void
  onSave: () => void
  saving: boolean
}

const SetListEditor: React.FC<SetListEditorProps> = ({
  items,
  onChange,
  onSave,
  saving,
}) => {
  const [searching, setSearching] = useState(false)
  const [searchOptions, setSearchOptions] = useState<{ label: string; value: number; item: any }[]>([])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const fetchSets = useMemo(
    () =>
      debounce(async (query: string) => {
        if (!query || query.trim().length === 0) {
          setSearchOptions([])
          return
        }
        setSearching(true)
        try {
          const res: any = await setService.list({
            page: 1,
            limit: 20,
            search: query.trim(),
            node_type: "folder",
            type: "system",
          })
          const list = res.data || []
          setSearchOptions(
            list.map((s: any) => ({
              label: `[#${s.id}] ${s.full_title || s.title}`,
              value: s.id,
              item: s,
            }))
          )
        } catch (e: any) {
          message.error("Lỗi tìm kiếm thư mục: " + (e.message || "Unknown"))
        } finally {
          setSearching(false)
        }
      }, 350),
    []
  )

  const handleAddSet = (value: number) => {
    const selected = searchOptions.find((opt) => opt.value === value)?.item
    if (!selected) return

    if (items.some((item) => Number(item.id) === Number(value))) {
      message.warning(`Thư mục #${value} đã có trong danh sách!`)
      return
    }

    const updated = [...items, selected]
    onChange(updated)
    message.success(`Đã thêm thư mục: ${selected.full_title || selected.title}`)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((it) => it.id === active.id)
      const newIndex = items.findIndex((it) => it.id === over.id)
      const updated = arrayMove(items, oldIndex, newIndex)
      onChange(updated)
    }
  }

  const handleMoveUp = (index: number) => {
    if (index === 0) return
    const updated = arrayMove(items, index, index - 1)
    onChange(updated)
  }

  const handleMoveDown = (index: number) => {
    if (index === items.length - 1) return
    const updated = arrayMove(items, index, index + 1)
    onChange(updated)
  }

  const handleRemove = (index: number) => {
    const updated = items.filter((_, idx) => idx !== index)
    onChange(updated)
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Search & Add */}
      <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-100 flex flex-col gap-2">
        <div className="text-xs font-semibold text-gray-700 flex items-center gap-1">
          <PlusOutlined className="text-primary" />
          <span>Thêm thư mục vào danh sách:</span>
        </div>
        <Select
          showSearch
          placeholder="Nhập tên hoặc ID thư mục để tìm kiếm..."
          filterOption={false}
          onSearch={fetchSets}
          onChange={handleAddSet}
          loading={searching}
          options={searchOptions}
          value={null}
          notFoundContent={searching ? <Spin size="small" /> : "Không tìm thấy thư mục"}
          className="w-full"
        />
      </div>

      {/* Items List */}
      {items.length === 0 ? (
        <Empty
          description="Danh sách đang trống. Hãy tìm kiếm và thêm thư mục ở trên."
          className="my-6"
        />
      ) : (
        <div className="max-h-[420px] overflow-y-auto pr-1">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              {items.map((item, index) => (
                <SortableSetItem
                  key={item.id}
                  item={item}
                  index={index}
                  totalCount={items.length}
                  onMoveUp={() => handleMoveUp(index)}
                  onMoveDown={() => handleMoveDown(index)}
                  onRemove={() => handleRemove(index)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      <div className="flex justify-end pt-2 border-t border-gray-100">
        <Button
          type="primary"
          onClick={onSave}
          loading={saving}
        >
          Lưu cấu hình
        </Button>
      </div>
    </div>
  )
}

export const SystemSetSettingsModal: React.FC<SystemSetSettingsModalProps> = ({
  open,
  onClose,
}) => {
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<string>("others_learning")

  // Setting records
  const [othersSetting, setOthersSetting] = useState<SettingItem | null>(null)
  const [featuredSetting, setFeaturedSetting] = useState<SettingItem | null>(null)

  // Items lists
  const [othersItems, setOthersItems] = useState<any[]>([])
  const [featuredItems, setFeaturedItems] = useState<any[]>([])

  // Saving states
  const [savingOthers, setSavingOthers] = useState(false)
  const [savingFeatured, setSavingFeatured] = useState(false)

  // Fetch settings on open
  useEffect(() => {
    if (!open) return

    setLoading(true)
    settingService
      .list({ group: "sets", limit: 50 })
      .then(async (res) => {
        const list = res.data || []
        const others = list.find((s) => s.name === "others_learning_ids")
        const featured = list.find((s) => s.name === "featured_ids")

        // Fetch detail with enriched set_items for others_learning_ids
        if (others) {
          try {
            const detail = await settingService.get(others.id)
            setOthersSetting(detail.data)
            setOthersItems(detail.data.set_items || [])
          } catch {
            setOthersSetting(others)
            const fallback = Array.isArray(others.payload)
              ? others.payload.map((id: number) => ({ id }))
              : []
            setOthersItems(fallback)
          }
        } else {
          setOthersSetting(null)
          setOthersItems([])
        }

        // Fetch detail with enriched set_items for featured_ids
        if (featured) {
          try {
            const detail = await settingService.get(featured.id)
            setFeaturedSetting(detail.data)
            setFeaturedItems(detail.data.set_items || [])
          } catch {
            setFeaturedSetting(featured)
            const fallback = Array.isArray(featured.payload)
              ? featured.payload.map((id: number) => ({ id }))
              : []
            setFeaturedItems(fallback)
          }
        } else {
          setFeaturedSetting(null)
          setFeaturedItems([])
        }
      })
      .catch((err) => {
        message.error("Lỗi khi tải cấu hình bộ từ: " + (err.message || "Unknown error"))
      })
      .finally(() => {
        setLoading(false)
      })
  }, [open])

  // Save others learning
  const handleSaveOthers = async () => {
    const payload = othersItems.map((it) => Number(it.id))
    setSavingOthers(true)
    try {
      if (othersSetting?.id) {
        await settingService.update(othersSetting.id, {
          payload,
          locked: othersSetting.locked,
        })
      } else {
        const res = await settingService.create({
          group: "sets",
          name: "others_learning_ids",
          payload,
          locked: 0,
        })
        setOthersSetting(res.data)
      }
      message.success("Đã cập nhật danh sách 'Người khác cũng đang học' thành công!")
    } catch (e: any) {
      message.error("Lỗi khi lưu: " + (e.message || "Unknown"))
    } finally {
      setSavingOthers(false)
    }
  }

  // Save featured
  const handleSaveFeatured = async () => {
    const payload = featuredItems.map((it) => Number(it.id))
    setSavingFeatured(true)
    try {
      if (featuredSetting?.id) {
        await settingService.update(featuredSetting.id, {
          payload,
          locked: featuredSetting.locked,
        })
      } else {
        const res = await settingService.create({
          group: "sets",
          name: "featured_ids",
          payload,
          locked: 0,
        })
        setFeaturedSetting(res.data)
      }
      message.success("Đã cập nhật danh sách 'Bộ từ nổi bật' thành công!")
    } catch (e: any) {
      message.error("Lỗi khi lưu: " + (e.message || "Unknown"))
    } finally {
      setSavingFeatured(false)
    }
  }

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <SettingOutlined className="text-primary text-base" />
          <span className="font-bold text-gray-800">Cấu hình Hiển thị Bộ từ trên Website</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      width={740}
      footer={[
        <Button key="close" onClick={onClose}>
          Đóng
        </Button>,
      ]}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <div className="flex flex-col gap-3 py-1">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: "others_learning",
                label: (
                  <span className="flex items-center gap-1.5 font-medium">
                    <StarOutlined className="text-blue-500" />
                    <span>Người khác cũng đang học</span>
                    <Tag color="blue" className="ml-1 text-[11px] py-0 px-1 font-semibold">
                      {othersItems.length}
                    </Tag>
                  </span>
                ),
                children: (
                  <SetListEditor
                    items={othersItems}
                    onChange={setOthersItems}
                    onSave={handleSaveOthers}
                    saving={savingOthers}
                  />
                ),
              },
              {
                key: "featured",
                label: (
                  <span className="flex items-center gap-1.5 font-medium">
                    <FireOutlined className="text-orange-500" />
                    <span>Bộ từ nổi bật</span>
                    <Tag color="orange" className="ml-1 text-[11px] py-0 px-1 font-semibold">
                      {featuredItems.length}
                    </Tag>
                  </span>
                ),
                children: (
                  <SetListEditor
                    items={featuredItems}
                    onChange={setFeaturedItems}
                    onSave={handleSaveFeatured}
                    saving={savingFeatured}
                  />
                ),
              },
            ]}
          />
        </div>
      </Spin>
    </Modal>
  )
}

export default SystemSetSettingsModal
